import {on} from 'delegated-events'
import {observe} from '@github/selector-observer'

import type {ToggleSwitchElement} from '../../../../components/primer/experimental/toggle-switch-element'
import type {ActionMenuElement} from '@primer/view-components/app/components/primer/alpha/action_menu/action_menu_element'
import {updatePermissionSummary} from './personal-access-token-summary'
import {announce} from '@github-ui/aria-live'

const PERMISSION_RANK: Record<string, number> = {none: 0, read: 1, write: 2, admin: 3}

// Toggles the disabled state of permission levels below the required level.
// For example, if requiredLevel is "write", "none" and "read" are updated.
function setInsufficientPermissionsDisabled(row: HTMLElement, requiredLevel: string, disabled: boolean) {
  const requiredRank = PERMISSION_RANK[requiredLevel] ?? 0
  for (const [permission, rank] of Object.entries(PERMISSION_RANK)) {
    if (rank < requiredRank) {
      setPermissionItemDisabled(row, permission, disabled)
    }
  }
}

// Disables or enables a single permission menu item using the ActionMenu API,
// and toggles the inactive reason description.
function setPermissionItemDisabled(row: HTMLElement, permission: string, disabled: boolean) {
  const actionMenu = row.querySelector<ActionMenuElement>('#action-selection-list-menu')
  if (!actionMenu) return

  const item = Array.from(row.querySelectorAll<HTMLLIElement>('[data-targets="action-list.items"]')).find(
    li => li.querySelector('button')?.getAttribute('data-permission') === permission,
  )
  if (!item) return

  if (disabled) {
    actionMenu.disableItem(item)
  } else {
    actionMenu.enableItem(item)
  }

  // Show/hide the inactive reason description
  const reason = item.querySelector<HTMLElement>('.js-agentic-inactive-reason')
  if (reason) reason.hidden = !disabled
}

// Updates the "Selected" and "Mandatory" labels on an agentic permission row.
// - At the minimum required level: only "Mandatory" is shown.
// - Above the minimum (user upgraded): both "Selected" and "Mandatory" are shown.
export function updateAgenticRowLabels(row: HTMLElement, currentLevel: string, requiredLevel: string) {
  const currentRank = PERMISSION_RANK[currentLevel] ?? 0
  const requiredRank = PERMISSION_RANK[requiredLevel] ?? 0
  const aboveMinimum = currentRank > requiredRank

  const selectedLabel = row.querySelector<HTMLElement>('.js-selected-label')
  if (selectedLabel) selectedLabel.hidden = !aboveMinimum

  const mandatoryLabel = row.querySelector<HTMLElement>('.js-mandatory-label')
  if (mandatoryLabel) mandatoryLabel.hidden = false
}

// Reads the required agentic permissions map from the server-rendered JSON
// Returns an empty object if the element is missing or JSON is malformed
function getAgenticPermissions(): Record<string, string> {
  const el = document.getElementById('js-agentic-integration-permissions')
  if (!el) return {}

  const raw = el.getAttribute('data-agentic-integration-permissions')
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>
    }
  } catch {
    // Malformed JSON - treat as no permissions
  }
  return {}
}

// Returns the minimum required permission level for a single resource,
// or null if the resource is not a mandatory permission.
export function getAgenticRequiredLevel(resource: string): string | null {
  return getAgenticPermissions()[resource] ?? null
}

// Returns true if the agentic toggle is currently on.
export function isAgenticModeOn(): boolean {
  const toggle = document.querySelector<ToggleSwitchElement>('.js-agentic-integration-toggle')
  if (!toggle) return false
  return toggle.isOn()
}

// Refreshes the permission summary counts for all permission categories
function updateAllPermissionSummaries() {
  updatePermissionSummary('repository')
  updatePermissionSummary('organization')
  updatePermissionSummary('user')
  updatePermissionSummary('business')
}

// Applies or removes mandatory agent permission constraints across all permission rows.
// - When enabling: selects the required level for each mandatory permission, marks it
//   as mandatory, disables insufficient levels, and updates labels.
// - When disabling: resets every row back to "No access", clears all mandatory
//   markers and labels, and re-enables all permission levels.
function setAgenticPermissions(isAgentic: boolean) {
  const agenticPermissions = getAgenticPermissions()

  if (isAgentic) {
    for (const [resource, requiredLevel] of Object.entries(agenticPermissions)) {
      // Find the button for the required permission level
      const input = document.querySelector(
        `.js-integration-permissions-selector [id^="integration_permission_"][data-resource="${resource}"][data-permission="${requiredLevel}"]`,
      )

      if (input && 'click' in input) {
        const el = input as HTMLElement

        // Click the button to select it via ActionMenu's internal logic
        el.click()

        const row = el.closest<HTMLElement>('.js-list-group-item')
        if (row) {
          // Mark the required level button as mandatory
          el.setAttribute('data-mandatory', 'true')

          const checked = row.querySelector<HTMLElement>('[id^=integration_permission_][aria-checked="true"]')
          const currentLevel = checked?.getAttribute('data-permission') || requiredLevel
          setInsufficientPermissionsDisabled(row, requiredLevel, true)
          updateAgenticRowLabels(row, currentLevel, requiredLevel)
        }
      }
    }
  } else {
    // Reset all permission rows back to default state
    const rows = document.querySelectorAll<HTMLElement>('.js-list-group-item')
    for (const row of rows) {
      row.classList.remove('disabled')
      row.removeAttribute('aria-disabled')
      row.querySelector<HTMLElement>('.js-selected-label')!.hidden = true
      row.querySelector<HTMLElement>('.js-mandatory-label')!.hidden = true

      setInsufficientPermissionsDisabled(row, 'admin', false) // clear out all disabled permissions

      for (const btn of row.querySelectorAll<HTMLElement>('[id^=integration_permission_]')) {
        btn.removeAttribute('data-mandatory')
      }

      // Click "No access" to reset the permission via ActionMenu
      const noAccessButton = row.querySelector<HTMLButtonElement>(
        '[id^="integration_permission_"][data-permission="none"]',
      )
      if (noAccessButton) noAccessButton.click()

      // Also reset the hidden form input that submits the permission value
      const hiddenInput = row.querySelector<HTMLInputElement>(
        'input[type="hidden"][name^="integration[default_permissions]"]',
      )
      if (hiddenInput) hiddenInput.value = 'none'
    }
  }
}

function setWebhookActive(active: boolean) {
  const checkbox = document.querySelector<HTMLInputElement>('.js-hook-active-checkbox')
  if (!checkbox) return

  if (checkbox.checked !== active) {
    checkbox.checked = active
    // Trigger existing handler that hides/show fields + required state
    checkbox.dispatchEvent(new Event('change', {bubbles: true}))
  }
}

function announceToScreenReader(toggle: ToggleSwitchElement) {
  const announceOn = toggle.getAttribute('data-announce-on') || ''
  const announceOff = toggle.getAttribute('data-announce-off') || ''

  const announcement = toggle.isOn() ? announceOn : announceOff

  if (announcement) announce(announcement)
}

// Handles the "Agent features" toggle click. When toggled on/off this:
// - Updates the page title and submit button text to reflect agentic/normal mode
// - Shows/hides agent source inputs
// - Sets the hidden form input so the server knows agentic mode is active
// - Sets active webhook off by default for agentic apps
// - Sets mandatory permissions for agentic apps
// - Refreshes the permission summary counts
on('click', '.js-agentic-integration-toggle', event => {
  const toggle = event.currentTarget as ToggleSwitchElement
  const isAgentic = toggle.isOn()

  // Update page title and submit button to agentic/normal text
  const title = document.querySelector<HTMLElement>('.js-integration-page-title')!
  const submit = document.querySelector<HTMLInputElement>('.js-integration-submit')!

  const text =
    (isAgentic ? title.getAttribute('data-agentic-integration-text') : title.getAttribute('data-integration-text')) ||
    ''

  title.textContent = text
  submit.value = text
  submit.setAttribute('data-disable-with', text)

  // Show/hide "Agent features" setup rows
  const agentSetupRows = document.querySelectorAll<HTMLElement>('.js-agent-setup-row')
  for (const row of agentSetupRows) {
    row.hidden = !isAgentic
  }

  // Set the hidden input so the server receives the agentic state on form submit
  const hiddenInput = document.querySelector<HTMLInputElement>('.js-agentic-integration-toggle-input')
  if (hiddenInput) {
    hiddenInput.value = isAgentic ? '1' : '0'
  }

  setWebhookActive(!isAgentic)
  setAgenticPermissions(isAgentic)
  updateAllPermissionSummaries()
  announceToScreenReader(toggle)
})

// When the page renders and the toggle is on (e.g. after a validation error),
// this restores the mandatory permissions labels without changing the permission values
observe('.js-agentic-integration-toggle', (el: Element) => {
  const toggle = el as ToggleSwitchElement
  if (!toggle.isOn()) return

  const agenticPermissions = getAgenticPermissions()

  // For each mandatory resource, restore the mandatory UI state:
  // mark the current selection as mandatory, disable insufficient levels, and set the correct labels.
  for (const [resource, requiredLevel] of Object.entries(agenticPermissions)) {
    const row = document
      .querySelector(
        `.js-integration-permissions-selector [id^="integration_permission_"][data-resource="${resource}"]`,
      )
      ?.closest<HTMLElement>('.js-list-group-item')
    if (!row) continue

    // Tag the required level button with data-mandatory so the permission summary
    // can distinguish mandatory permissions from user-selected ones.
    const requiredBtn = row.querySelector<HTMLElement>(
      `[id^=integration_permission_][data-resource="${resource}"][data-permission="${requiredLevel}"]`,
    )
    if (requiredBtn) requiredBtn.setAttribute('data-mandatory', 'true')

    // Disable levels below the minimum and update row labels
    const checked = row.querySelector<HTMLElement>('[id^=integration_permission_][aria-checked="true"]')
    const currentLevel = checked?.getAttribute('data-permission') || requiredLevel
    setInsufficientPermissionsDisabled(row, requiredLevel, true)
    updateAgenticRowLabels(row, currentLevel, requiredLevel)
  }

  // Refresh the permission summary counts for all categories
  updateAllPermissionSummaries()
})

// When agentic mode is active and the user changes a permission, defer (setTimeout) the
// label update so it runs after toggleSelectedLabel has finished updating the
// row's label state.
on('itemActivated', '#action-selection-list-menu', (event: CustomEvent) => {
  if (!isAgenticModeOn()) return

  const selectedItem = event.detail.item as HTMLLIElement
  const itemButton = selectedItem.querySelector<HTMLButtonElement>('button')
  if (!itemButton) return

  const permission = itemButton.getAttribute('data-permission') || 'none'
  const resource = itemButton.getAttribute('data-resource') || ''
  const requiredLevel = getAgenticRequiredLevel(resource)
  if (!requiredLevel) return

  const row = itemButton.closest<HTMLElement>('.js-list-group-item')
  if (row) setTimeout(() => updateAgenticRowLabels(row, permission, requiredLevel), 0)
})
